import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { Sun, Moon, Plus, Pencil, Trash2, X, AlertTriangle, FileSpreadsheet, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import {
  getFaturamentoBruto, getResumoFaturamentoBruto,
  postFaturamentoBruto, putFaturamentoBruto, deleteFaturamentoBruto,
} from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { KpiRow } from "../../components/dashboard/KpiCard";
import { EMPRESAS_FATURAMENTO_BRUTO } from "../../constants/faturamentoBruto";
import { MESES_ABREV, formatCurrency } from "../../utils/orcamento";

// Paleta padrão da plataforma (mesma usada nas demais telas de Gestão Orçamentária).
const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  bgActive: isLight ? "#F1E9FE" : "#241A3D",
  success: "#10B981",
  danger: "#EF4444",
});

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);

const OPCOES_SETOR = ["Público", "Privado", "3º Setor"];
const OPCOES_TIPO_BACKLOG = ["Backlog 1", "Backlog 2", "Backlog 3"];

// Mantém visível um valor já gravado que não esteja na lista (cadastros antigos em texto livre).
function opcoesComValorAtual(opcoes, atual) {
  return atual && !opcoes.includes(atual) ? [atual, ...opcoes] : opcoes;
}

// Colunas ordenáveis da tabela: chave → como extrair o valor comparável do projeto.
const COLUNAS_ORDENAVEIS = [
  { id: "codigo", label: "Código", valor: (p) => Number(p.codigo) || 0, numerico: true },
  { id: "empresa", label: "Empresa", valor: (p) => p.empresa || "" },
  { id: "nome_projeto", label: "Projeto", valor: (p) => p.nome_projeto || "", alinhar: "left" },
  { id: "tipo_contrato", label: "Tipo", valor: (p) => (p.tipo_contrato === "backlog" ? "Backlog" : "Nova Venda") },
  { id: "tipo_backlog", label: "Tipo Backlog", valor: (p) => p.tipo_backlog || "" },
  { id: "gerente_projeto", label: "Gerente", valor: (p) => p.gerente_projeto || "" },
  { id: "saldo_total", label: "Saldo Total", valor: (p) => Number(p.saldo_total) || 0, numerico: true },
];

const VISOES = [
  { id: "com_margem", label: "Faturamento (com margem)" },
  { id: "sem_margem", label: "Custo sem Margem" },
  { id: "liquido", label: "Líquido" },
];

const FORM_VAZIO = {
  empresa: EMPRESAS_FATURAMENTO_BRUTO[0],
  codigo: "",
  setor: "",
  tipo_contrato: "backlog",
  tipo_backlog: "",
  nome_projeto: "",
  valor_contrato_2025: "0",
  saldo_novos_aditivos: "0",
  saldo_previsao_2026: "0",
  gerente_projeto: "",
  margem_projetada_pct: "0",
  meses_com_margem: Array(12).fill("0"),
  tem_sem_margem: false,
  meses_sem_margem: Array(12).fill("0"),
};

// meses_com_margem/meses_sem_margem vêm do backend como valores brutos informados no
// cadastro (mesmos números que reabrem no formulário de edição). As 3 visões da tabela são
// sempre derivadas deles aqui: Faturamento (com margem) = com_margem;
// Custo sem Margem = sem_margem; Líquido = com_margem − sem_margem.
function valorDoMes(projeto, visao, mes) {
  const comMargem = Number(projeto.meses_com_margem?.[mes] || 0);
  const semMargem = Number(projeto.meses_sem_margem?.[mes] || 0);
  if (visao === "com_margem") return comMargem;
  if (visao === "sem_margem") return semMargem;
  return comMargem - semMargem;
}

function totalProjeto(projeto, visao) {
  let total = 0;
  for (let mes = 1; mes <= 12; mes++) total += valorDoMes(projeto, visao, mes);
  return total;
}

function corValor(C, visao, valor) {
  if (visao === "com_margem") return C.success;
  if (visao === "sem_margem") return C.danger;
  return valor >= 0 ? C.success : C.danger;
}

export default function FaturamentoBruto() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();

  const [ano, setAno] = useState(ANO_ATUAL+1);
  const [empresaFiltro, setEmpresaFiltro] = useState("");
  const [visao, setVisao] = useState("com_margem");

  const [projetos, setProjetos] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [erroForm, setErroForm] = useState("");
  const [salvando, setSalvando] = useState(false);
  // null = ordem padrão (empresa, depois código)
  const [ordenacao, setOrdenacao] = useState(null);

  async function carregarTudo() {
    setLoading(true);
    setErro("");
    try {
      const [lista, resumoDados] = await Promise.all([
        getFaturamentoBruto(ano, empresaFiltro),
        getResumoFaturamentoBruto(ano, empresaFiltro),
      ]);
      setProjetos(lista || []);
      setResumo(resumoDados || null);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregarTudo(); }, [ano, empresaFiltro]);

  const projetosOrdenados = useMemo(() => {
    const padrao = (a, b) => a.empresa.localeCompare(b.empresa) || a.codigo - b.codigo;
    const coluna = ordenacao && COLUNAS_ORDENAVEIS.find((c) => c.id === ordenacao.coluna);
    if (!coluna) return [...projetos].sort(padrao);
    const sinal = ordenacao.direcao === "asc" ? 1 : -1;
    return [...projetos].sort((a, b) => {
      const va = coluna.valor(a);
      const vb = coluna.valor(b);
      const cmp = coluna.numerico ? va - vb : va.localeCompare(vb, "pt-BR", { sensitivity: "base", numeric: true });
      return cmp * sinal || padrao(a, b);
    });
  }, [projetos, ordenacao]);

  // Clique no cabeçalho: asc → desc → volta à ordem padrão.
  function alternarOrdenacao(colunaId) {
    setOrdenacao((atual) => {
      if (!atual || atual.coluna !== colunaId) return { coluna: colunaId, direcao: "asc" };
      if (atual.direcao === "asc") return { coluna: colunaId, direcao: "desc" };
      return null;
    });
  }

  const totaisPorMes = useMemo(() => {
    const totais = Array(12).fill(0);
    projetosOrdenados.forEach((p) => {
      for (let mes = 1; mes <= 12; mes++) totais[mes - 1] += valorDoMes(p, visao, mes);
    });
    return totais;
  }, [projetosOrdenados, visao]);
  const totalGeral = totaisPorMes.reduce((acc, v) => acc + v, 0);
  const saldoTotalGeral = projetosOrdenados.reduce((acc, p) => acc + Number(p.saldo_total || 0), 0);

  // ── Exportar para Excel (backup/consulta caso o BD apresente problema) ────
  function exportarParaExcel() {
    const linhas = projetosOrdenados.map((p) => {
      const linha = {
        "Código": p.codigo,
        "Empresa": p.empresa,
        "Projeto": p.nome_projeto,
        "Tipo": p.tipo_contrato === "backlog" ? "Backlog" : "Nova Venda",
        "Tipo Backlog": p.tipo_backlog || "",
        "Gerente": p.gerente_projeto || "",
        "Saldo Total": p.saldo_total,
      };
      MESES_ABREV.forEach((m, i) => { linha[m] = valorDoMes(p, visao, i + 1); });
      linha["Total"] = totalProjeto(p, visao);
      return linha;
    });
    linhas.push({
      "Código": "", "Empresa": "", "Projeto": "Total Geral", "Tipo": "", "Tipo Backlog": "", "Gerente": "", "Saldo Total": saldoTotalGeral,
      ...Object.fromEntries(MESES_ABREV.map((m, i) => [m, totaisPorMes[i]])),
      "Total": totalGeral,
    });

    const worksheet = XLSX.utils.json_to_sheet(linhas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Faturamento Bruto".slice(0, 31));
    const nomeArquivo = `Faturamento_Bruto_${VISOES.find((v) => v.id === visao)?.label || visao}_${empresaFiltro || "Todas"}_${ano}`.replace(/[^\w]+/g, "_");
    XLSX.writeFile(workbook, `${nomeArquivo}.xlsx`);
  }

  const kpiItems = [
    { label: "Total Contratado", value: loading || !resumo ? "—" : formatCurrency(resumo.total_contratado), color: C.text },
    { label: "Faturamento Projetado no Ano", value: loading || !resumo ? "—" : formatCurrency(resumo.faturamento_projetado_ano), color: C.success },
    { label: "Custo sem Margem no Ano", value: loading || !resumo ? "—" : formatCurrency(resumo.custo_sem_margem_ano), color: C.danger },
    { label: "Resultado Líquido Projetado", value: loading || !resumo ? "—" : formatCurrency(resumo.resultado_liquido_projetado), color: C.brand },
    { label: "Total de Projetos", value: loading || !resumo ? "—" : String(resumo.total_projetos), color: C.text },
  ];

  // ── Modal: abrir / salvar / excluir ─────────────────────────────────────
  function abrirModal(projeto = null) {
    setErroForm("");
    if (projeto) {
      const temSemMargem = Object.values(projeto.meses_sem_margem || {}).some((v) => Number(v) !== 0);
      setEditando(projeto);
      setForm({
        empresa: projeto.empresa,
        codigo: String(projeto.codigo),
        setor: projeto.setor || "",
        tipo_contrato: projeto.tipo_contrato,
        tipo_backlog: projeto.tipo_backlog || "",
        nome_projeto: projeto.nome_projeto,
        valor_contrato_2025: String(projeto.valor_contrato_2025),
        saldo_novos_aditivos: String(projeto.saldo_novos_aditivos),
        saldo_previsao_2026: String(projeto.saldo_previsao_2026),
        gerente_projeto: projeto.gerente_projeto || "",
        margem_projetada_pct: String(Math.round(Number(projeto.margem_projetada || 0) * 10000) / 100),
        meses_com_margem: Array.from({ length: 12 }, (_, i) => String(projeto.meses_com_margem?.[i + 1] ?? 0)),
        tem_sem_margem: temSemMargem,
        meses_sem_margem: Array.from({ length: 12 }, (_, i) => String(projeto.meses_sem_margem?.[i + 1] ?? 0)),
      });
    } else {
      setEditando(null);
      setForm({ ...FORM_VAZIO, meses_com_margem: Array(12).fill("0"), meses_sem_margem: Array(12).fill("0") });
    }
    setModalAberto(true);
  }

  async function salvarProjeto() {
    const codigo = parseInt(form.codigo, 10);
    const valorContrato2025 = parseFloat(form.valor_contrato_2025) || 0;
    const saldoNovosAditivos = parseFloat(form.saldo_novos_aditivos) || 0;
    const saldoPrevisao2026 = parseFloat(form.saldo_previsao_2026) || 0;
    const margemProjetadaPct = form.margem_projetada_pct === "" ? 0 : parseFloat(form.margem_projetada_pct);

    if (!form.empresa) return setErroForm("Selecione a empresa.");
    if (!Number.isInteger(codigo) || codigo <= 0) return setErroForm("Informe um código de projeto válido.");
    if (!form.nome_projeto.trim()) return setErroForm("Informe o nome do projeto.");
    if (valorContrato2025 < 0 || saldoNovosAditivos < 0 || saldoPrevisao2026 < 0) {
      return setErroForm("Valor do Contrato, Saldo Novos/Aditivos e Saldo Previsão não podem ser negativos.");
    }
    if (Number.isNaN(margemProjetadaPct)) return setErroForm("Informe uma Margem Projetada válida.");

    const saldoTotal = valorContrato2025 + saldoNovosAditivos;
    const somaMesesComMargem = form.meses_com_margem.reduce((acc, v) => acc + (parseFloat(v) || 0), 0);
    if (somaMesesComMargem > saldoTotal + 0.01) {
      return setErroForm(
        `A soma da distribuição mensal de Faturamento (com margem) (${formatCurrency(somaMesesComMargem)}) não pode ultrapassar o Saldo Total do contrato (${formatCurrency(saldoTotal)}).`
      );
    }

    setSalvando(true);
    setErroForm("");
    try {
      const mesesComMargem = {};
      const mesesSemMargem = {};
      for (let i = 0; i < 12; i++) {
        mesesComMargem[i + 1] = parseFloat(form.meses_com_margem[i]) || 0;
        mesesSemMargem[i + 1] = form.tem_sem_margem ? (parseFloat(form.meses_sem_margem[i]) || 0) : 0;
      }
      const payload = {
        empresa: form.empresa,
        codigo,
        setor: form.setor.trim() || null,
        tipo_contrato: form.tipo_contrato,
        tipo_backlog: form.tipo_backlog.trim() || null,
        nome_projeto: form.nome_projeto.trim(),
        valor_contrato_2025: valorContrato2025,
        saldo_novos_aditivos: saldoNovosAditivos,
        saldo_previsao_2026: saldoPrevisao2026,
        gerente_projeto: form.gerente_projeto.trim() || null,
        margem_projetada: margemProjetadaPct / 100,
        ano_referencia: ano,
        meses_com_margem: mesesComMargem,
        meses_sem_margem: mesesSemMargem,
      };
      if (editando) await putFaturamentoBruto(editando.id, payload);
      else await postFaturamentoBruto(payload);
      setModalAberto(false);
      await carregarTudo();
    } catch (e) {
      setErroForm(e.message);
    } finally {
      setSalvando(false);
    }
  }

  async function excluirProjeto(projeto) {
    if (!projeto) return;
    if (!window.confirm(`Excluir o projeto "${projeto.nome_projeto}"? Essa ação não pode ser desfeita.`)) return;
    try {
      await deleteFaturamentoBruto(projeto.id);
      await carregarTudo();
    } catch (e) {
      setErro(e.message);
    }
  }

  // Rótulos do modal acompanham o ano de referência (ano selecionado, ou o do projeto em edição).
  const anoForm = Number(editando?.ano_referencia) || ano;
  const saldoTotalForm = (parseFloat(form.valor_contrato_2025) || 0) + (parseFloat(form.saldo_novos_aditivos) || 0);
  const somaMesesComMargemForm = form.meses_com_margem.reduce((acc, v) => acc + (parseFloat(v) || 0), 0);
  const excedeuSaldoTotal = somaMesesComMargemForm > saldoTotalForm + 0.01;

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        {/* Header */}
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)",
          color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Faturamento Bruto</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select
              value={empresaFiltro}
              onChange={(e) => setEmpresaFiltro(e.target.value)}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              <option value="" style={{ color: "#0F172A" }}>Todas as Empresas</option>
              {EMPRESAS_FATURAMENTO_BRUTO.map((e) => <option key={e} value={e} style={{ color: "#0F172A" }}>{e}</option>)}
            </select>
            <select
              value={ano}
              onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
            }}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />}
              {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button
              onClick={() => navigate("/inicio")}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 14px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              Menu Principal
            </button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          {erro && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`,
              border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13,
            }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          {/* KPIs — sempre visíveis, resumem as 3 visões ao mesmo tempo */}
          <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <KpiRow items={kpiItems} C={C} />
          </section>

          {/* Tabela principal */}
          <section>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>
                Faturamento Bruto · {empresaFiltro || "Todas as Empresas"} · {ano}
              </h2>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                 <button
                  onClick={exportarParaExcel}
                  disabled={loading || projetosOrdenados.length === 0}
                  title="Exportar dados para Excel"
                  style={{ ...btnStyle(C.success, true), opacity: loading || projetosOrdenados.length === 0 ? 0.45 : 1, cursor: loading || projetosOrdenados.length === 0 ? "not-allowed" : "pointer" }}
                >
                  <FileSpreadsheet size={15} /> Exportar Excel
                </button>
                <div style={{ display: "flex", gap: 6, background: C.bg, padding: 4, borderRadius: 10, border: `1px solid ${C.border}` }}>
                  {VISOES.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setVisao(v.id)}
                      style={{
                        padding: "7px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                        background: visao === v.id ? C.brand : "transparent",
                        color: visao === v.id ? "#fff" : C.text,
                        fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                      }}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
                <button onClick={() => abrirModal(null)} style={btnStyle(C.brand)}>
                  <Plus size={15} /> Novo projeto
                </button>
              </div>
            </div>

            {loading ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>
            ) : projetosOrdenados.length === 0 ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
                Nenhum projeto cadastrado para {ano}. Clique em "Novo projeto" para começar.
              </div>
            ) : (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 1800 }}>
                    <thead>
                      <tr>
                        {COLUNAS_ORDENAVEIS.map((col) => {
                          const ativa = ordenacao?.coluna === col.id;
                          const Icone = !ativa ? ArrowUpDown : ordenacao.direcao === "asc" ? ArrowUp : ArrowDown;
                          return (
                            <th
                              key={col.id}
                              onClick={() => alternarOrdenacao(col.id)}
                              title={`Ordenar por ${col.label}`}
                              style={{
                                ...thStyle(C),
                                ...(col.alinhar ? { textAlign: col.alinhar } : {}),
                                cursor: "pointer", userSelect: "none",
                                color: ativa ? C.brand : C.muted,
                              }}
                            >
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                {col.label}
                                <Icone size={12} style={{ opacity: ativa ? 1 : 0.45 }} />
                              </span>
                            </th>
                          );
                        })}
                        {MESES_ABREV.map((m) => <th key={m} style={thStyle(C)}>{m}</th>)}
                        <th style={thStyle(C)}>Total</th>
                        <th style={thStyle(C)}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projetosOrdenados.map((p) => {
                        const total = totalProjeto(p, visao);
                        return (
                          <tr key={p.id}>
                            <td style={tdStyle(C)}>{p.codigo}</td>
                            <td style={tdStyle(C)}>{p.empresa}</td>
                            <td style={{ ...tdStyle(C), textAlign: "left" }}>{p.nome_projeto}</td>
                            <td style={tdStyle(C)}>
                              {p.tipo_contrato === "backlog" ? "Backlog" : "Nova Venda"}
                            </td>
                            <td style={tdStyle(C)}>{p.tipo_backlog || "—"}</td>
                            <td style={tdStyle(C)}>{p.gerente_projeto || "—"}</td>
                            <td style={tdStyle(C)}>{formatCurrency(p.saldo_total)}</td>
                            {MESES_ABREV.map((_, i) => {
                              const v = valorDoMes(p, visao, i + 1);
                              return <td key={i} style={{ ...tdStyle(C), color: corValor(C, visao, v) }}>{formatCurrency(v)}</td>;
                            })}
                            <td style={{ ...tdStyle(C), fontWeight: 700, color: corValor(C, visao, total) }}>{formatCurrency(total)}</td>
                            <td style={tdStyle(C)}>
                              <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                <IconBtn title="Editar projeto" onClick={() => abrirModal(p)} C={C}><Pencil size={13} /></IconBtn>
                                <IconBtn title="Excluir projeto" onClick={() => excluirProjeto(p)} C={C} danger><Trash2 size={13} /></IconBtn>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ fontWeight: 800, borderTop: `2px solid ${C.border}` }}>
                        <td style={{ ...tdStyle(C, true), textAlign: "left" }} colSpan={6}>Total Geral</td>
                        <td style={tdStyle(C, true)}>{formatCurrency(saldoTotalGeral)}</td>
                        {totaisPorMes.map((v, i) => (
                          <td key={i} style={{ ...tdStyle(C, true), color: corValor(C, visao, v) }}>{formatCurrency(v)}</td>
                        ))}
                        <td style={{ ...tdStyle(C, true), color: corValor(C, visao, totalGeral) }}>{formatCurrency(totalGeral)}</td>
                        <td style={tdStyle(C)} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </section>
        </main>
      </div>

      {/* Modal: Novo/Editar Projeto */}
      {modalAberto && (
        <ModalShell C={C} title={editando ? "Editar Projeto" : "Novo Projeto"} onClose={() => setModalAberto(false)} maxWidth={800}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <p style={{ margin: 0, fontSize: 11, color: C.muted }}>Ano de referência: <strong style={{ color: C.text }}>{ano}</strong></p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Campo label="Empresa" C={C}>
                <select style={inputStyle(C)} value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })}>
                  {EMPRESAS_FATURAMENTO_BRUTO.map((e) => <option key={e} value={e}>{e}</option>)}
                </select>
              </Campo>
              <Campo label="Código" C={C}>
                <input style={inputStyle(C)} type="number" step="1" value={form.codigo}
                  onChange={(e) => setForm({ ...form, codigo: e.target.value })} />
              </Campo>
              <Campo label="Setor" C={C}>
                <select style={inputStyle(C)} value={form.setor} onChange={(e) => setForm({ ...form, setor: e.target.value })}>
                  <option value="">Selecione...</option>
                  {opcoesComValorAtual(OPCOES_SETOR, form.setor).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </Campo>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Campo label="Backlog ou Nova Venda" C={C}>
                <select style={inputStyle(C)} value={form.tipo_contrato} onChange={(e) => setForm({ ...form, tipo_contrato: e.target.value })}>
                  <option value="backlog">Backlog</option>
                  <option value="nova_venda">Nova Venda</option>
                </select>
              </Campo>
              <Campo label="Tipo Backlog" C={C}>
                <select style={inputStyle(C)} value={form.tipo_backlog} onChange={(e) => setForm({ ...form, tipo_backlog: e.target.value })}>
                  <option value="">Selecione...</option>
                  {opcoesComValorAtual(OPCOES_TIPO_BACKLOG, form.tipo_backlog).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </Campo>
            </div>

            <Campo label="Nome do Projeto" C={C}>
              <input style={inputStyle(C)} type="text" value={form.nome_projeto}
                onChange={(e) => setForm({ ...form, nome_projeto: e.target.value })} />
            </Campo>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Campo label={`Valor do Contrato em 31.12.${anoForm - 1}`} C={C}>
                <input style={inputStyle(C)} type="number" min="0" step="0.01" value={form.valor_contrato_2025}
                  onChange={(e) => setForm({ ...form, valor_contrato_2025: e.target.value })} />
              </Campo>
              <Campo label={`Saldo Contrato Novos/Aditivos ${anoForm}`} C={C}>
                <input style={inputStyle(C)} type="number" min="0" step="0.01" value={form.saldo_novos_aditivos}
                  onChange={(e) => setForm({ ...form, saldo_novos_aditivos: e.target.value })} />
              </Campo>
              <Campo label="Saldo Total" C={C}>
                <div style={{ ...inputStyle(C), background: C.bgActive, fontWeight: 700, textAlign: "right" }}>
                  {formatCurrency(saldoTotalForm)}
                </div>
              </Campo>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Campo label={`Saldo Contrato Previsão 31.12.${anoForm}`} C={C}>
                <input style={inputStyle(C)} type="number" min="0" step="0.01" value={form.saldo_previsao_2026}
                  onChange={(e) => setForm({ ...form, saldo_previsao_2026: e.target.value })} />
              </Campo>
              <Campo label="Gerente de Projetos" C={C}>
                <input style={inputStyle(C)} type="text" value={form.gerente_projeto}
                  onChange={(e) => setForm({ ...form, gerente_projeto: e.target.value })} />
              </Campo>
            </div>

            <Campo label="Margem Projetada (%)" C={C}>
              <input style={inputStyle(C)} type="number" step="0.01" placeholder="Ex: 61,56" value={form.margem_projetada_pct}
                onChange={(e) => setForm({ ...form, margem_projetada_pct: e.target.value })} />
            </Campo>

            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Faturamento (com margem) — distribuição mensal
                </p>
                <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: excedeuSaldoTotal ? C.danger : C.muted }}>
                  Distribuído: {formatCurrency(somaMesesComMargemForm)} / Saldo Total: {formatCurrency(saldoTotalForm)}
                </p>
              </div>
              <MesesGrid
                C={C}
                valores={form.meses_com_margem}
                onChange={(i, v) => {
                  const novo = [...form.meses_com_margem];
                  novo[i] = v;
                  setForm({ ...form, meses_com_margem: novo });
                }}
              />
              {excedeuSaldoTotal && (
                <p style={{ margin: "6px 0 0", fontSize: 11, color: C.danger }}>
                  A distribuição mensal ultrapassa o Saldo Total do contrato.
                </p>
              )}
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text, cursor: "pointer" }}>
              <input type="checkbox" checked={form.tem_sem_margem}
                onChange={(e) => setForm({ ...form, tem_sem_margem: e.target.checked })} />
              Este projeto tem serviço terceirizado sem margem?
            </label>

            {form.tem_sem_margem && (
              <div>
                <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Custo sem Margem — distribuição mensal
                </p>
                <MesesGrid
                  C={C}
                  valores={form.meses_sem_margem}
                  onChange={(i, v) => {
                    const novo = [...form.meses_sem_margem];
                    novo[i] = v;
                    setForm({ ...form, meses_sem_margem: novo });
                  }}
                />
                <p style={{ margin: "6px 0 0", fontSize: 11, color: C.muted }}>Normalmente valores negativos (custo).</p>
              </div>
            )}

            {erroForm && <p style={{ color: C.danger, fontSize: 12, margin: 0 }}>{erroForm}</p>}
          </div>
          <ModalFooter
            C={C} salvando={salvando}
            onCancel={() => setModalAberto(false)}
            onSave={salvarProjeto}
            labelSalvar={editando ? "Atualizar" : "Salvar projeto"}
          />
        </ModalShell>
      )}
    </div>
  );
}

// ── Helpers de UI locais ─────────────────────────────────────────────────────

function MesesGrid({ C, valores, onChange }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
      {MESES_ABREV.map((m, i) => (
        <Campo key={m} label={m} C={C}>
          <input style={inputStyle(C)} type="number" step="0.01" value={valores[i]}
            onChange={(e) => onChange(i, e.target.value)} />
        </Campo>
      ))}
    </div>
  );
}

function Campo({ label, children, C }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function inputStyle(C) {
  return {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: "9px 12px", fontSize: 13, color: C.text, fontFamily: "inherit", outline: "none",
  };
}

function btnStyle(color, secondary = false) {
  return {
    display: "flex", alignItems: "center", gap: 6,
    background: secondary ? "transparent" : color,
    border: secondary ? `1px solid ${color}` : "none",
    color: secondary ? color : "#fff",
    borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700,
    fontFamily: "inherit", cursor: "pointer",
  };
}

function thStyle(C) {
  return {
    padding: "8px 10px", fontSize: 11, fontWeight: 700, color: C.muted,
    textTransform: "uppercase", letterSpacing: "0.04em", textAlign: "right",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
  };
}

function tdStyle(C, strong = false) {
  return {
    padding: "8px 10px", fontSize: 12, color: C.text, textAlign: "right",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
    fontWeight: strong ? 700 : 400,
  };
}

function IconBtn({ children, onClick, title, C, danger = false }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 26, height: 26, borderRadius: 6, border: `1px solid ${C.border}`,
        background: "transparent", color: danger ? C.danger : C.text, cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function ModalShell({ title, children, onClose, C, maxWidth = 480 }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth, boxShadow: "0 24px 64px rgba(0,0,0,0.4)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{title}</p>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}>
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({ C, salvando, onCancel, onSave, labelSalvar }) {
  return (
    <div style={{ display: "flex", gap: 10, marginTop: 20, position: "sticky", bottom: 0, background: C.card, paddingTop: 10 }}>
      <button style={{ flex: 1, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 0", color: C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
        onClick={onCancel}>
        Cancelar
      </button>
      <button style={{ flex: 1, background: C.brand, border: "none", borderRadius: 8, padding: "10px 0", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: salvando ? 0.6 : 1 }}
        onClick={onSave} disabled={salvando}>
        {salvando ? "Salvando..." : labelSalvar}
      </button>
    </div>
  );
}
